import { useEffect, useRef, useCallback, useState } from 'react';
import { tokenStore } from '@/shared/auth';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const DEFAULT_MAX_FAILURES = 3;
const DEFAULT_RECONNECT_DELAY_MS = 3000;

interface UseEventStreamOptions<T> {
  /** Path relative to API_BASE (e.g. '/notifications/stream') */
  path: string;
  /** SSE event name to listen for (e.g. 'notification') */
  eventName: string;
  /** Whether the stream should be active */
  enabled: boolean;
  /** Called for each parsed event */
  onEvent: (data: T) => void;
  /** Max consecutive failures before switching to fallback (default: 3) */
  maxConsecutiveFailures?: number;
  /** Delay between reconnection attempts in ms (default: 3000) */
  reconnectDelayMs?: number;
}

/**
 * Generic SSE stream hook using fetch + ReadableStream (not EventSource).
 * Uses Authorization header so no token leaks in query params.
 * Auto-reconnects on disconnect with Last-Event-ID for resumability.
 * After maxConsecutiveFailures, sets isFallback=true so consumers
 * can switch to polling.
 */
export const useEventStream = <T>({
  path,
  eventName,
  enabled,
  onEvent,
  maxConsecutiveFailures = DEFAULT_MAX_FAILURES,
  reconnectDelayMs = DEFAULT_RECONNECT_DELAY_MS,
}: UseEventStreamOptions<T>) => {
  const [isFallback, setIsFallback] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const mountedRef = useRef(true);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connect = useCallback(async () => {
    if (!mountedRef.current || !enabled) {
      return;
    }

    const accessToken = tokenStore.getAccessToken();
    if (!accessToken) {
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };
    if (lastEventIdRef.current) {
      headers['Last-Event-ID'] = lastEventIdRef.current;
    }

    try {
      const response = await fetch(`${API_BASE}${path}`, {
        headers,
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE stream returned ${response.status}`);
      }

      consecutiveFailuresRef.current = 0;
      setIsFallback(false);

      const reader = response.body?.getReader();
      if (!reader) {
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (mountedRef.current) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        let currentEvent = '';
        let currentData = '';
        let currentId = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line.startsWith('id: ')) {
            currentId = line.slice(4);
          } else if (line === '') {
            if (currentEvent === eventName && currentData) {
              try {
                const parsed = JSON.parse(currentData) as T;
                if (currentId) {
                  lastEventIdRef.current = currentId;
                }
                onEvent(parsed);
              } catch {
                // Skip malformed events
              }
            }
            currentEvent = '';
            currentData = '';
            currentId = '';
          }
        }
      }
    } catch (_error) {
      if (controller.signal.aborted) {
        return;
      }
    }

    // Stream ended or failed — reconnect unless we've hit the max
    if (mountedRef.current && enabled) {
      consecutiveFailuresRef.current++;
      if (consecutiveFailuresRef.current >= maxConsecutiveFailures) {
        setIsFallback(true);
        return;
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        void connect();
      }, reconnectDelayMs);
    }
  }, [enabled, onEvent, path, eventName, maxConsecutiveFailures, reconnectDelayMs]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled && !isFallback) {
      void connect();
    }
    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [enabled, isFallback, connect]);

  return { isFallback };
};
